/**
 * Hot-read window (a.k.a. "read from the write replica" window).
 *
 * After a write (POST/PUT/DELETE) the backend needs a short grace period during
 * which subsequent GETs must be routed to the write replica (via the
 * `from-write-server=1` query param) so the client does not read a lagging
 * read replica. See the API layer contract in CLAUDE.md:
 * "After write operations, GET requests send from-write-server=1 for 5 seconds".
 *
 * The window is modelled by a single expiry timestamp rather than a boolean
 * flag. Openness is *derived* from the expiry (`Date.now() < expiresAt`), which
 * makes it immune to the timer-ordering race that a bare boolean flag suffers
 * from: even a stray/late timer callback cannot report the window as open when
 * it has already expired, nor closed while it is still within its window.
 *
 * Semantics:
 *  - open(ms) is **extend-only**: it can only ever lengthen the remaining
 *    window, never shorten it. A short manual hot-read (1s) firing during an
 *    active longer write window (5s) therefore leaves the longer window intact.
 *  - close() immediately ends the window (clears the timer, resets the expiry).
 *  - A single timer handle is tracked and cleared before re-arming, so
 *    overlapping opens cannot leave orphaned timers that clip a later window.
 *
 * Assumption: relies on `Date.now()` being monotonic-enough over the sub-10s
 * horizon of a hot-read window. A backward wall-clock adjustment (NTP/DST) could
 * momentarily mis-size a window; this is acceptable for a best-effort read-your-
 * writes hint and matches the trade-off in the original design. The tracked
 * timer also actively resets the expiry at the natural end of the window, so the
 * stored timestamp never lingers.
 */
export class HotReadWindow {
  private expiresAt = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;

  /**
   * Open (or extend) the window so it stays open for at least `durationMs` more.
   * Extend-only: a request for a window that would end earlier than the current
   * one is a no-op, so a shorter window can never truncate a longer active one.
   */
  open(durationMs: number): void {
    const candidateExpiry = Date.now() + durationMs;
    // Extend-only: never shorten an already-longer active window.
    if (candidateExpiry <= this.expiresAt) {
      return;
    }
    this.expiresAt = candidateExpiry;
    this.armTimer();
  }

  /** Immediately close the window. */
  close(): void {
    this.clearTimer();
    this.expiresAt = 0;
  }

  /** Whether the window is currently open. Derived from the expiry timestamp. */
  isOpen(): boolean {
    return Date.now() < this.expiresAt;
  }

  private armTimer(): void {
    this.clearTimer();
    const remainingMs = this.expiresAt - Date.now();
    this.timer = setTimeout(() => {
      this.timer = undefined;
      this.expiresAt = 0;
    }, remainingMs);
  }

  private clearTimer(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }
}

/** Window length applied after a write (POST/PUT/DELETE) response. */
export const WRITE_HOT_READ_WINDOW_MS = 5000;

/** Window length applied by a manual `Api.hotRead(true)` call. */
export const MANUAL_HOT_READ_WINDOW_MS = 1000;
